import type { Spell } from '@character-creator/types';

export const level4Spells: Spell[] = [
  {
    id: 'arcane-eye',
    name: 'Arcane Eye',
    level: 4,
    school: 'divination',
    ritual: false,
    castingTime: '1 action',
    range: '30 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a bit of bat fur' },
    duration: '1 hour',
    concentration: true,
    description: "You create an invisible, magical eye within range that hovers in the air for the duration. You mentally receive visual information from the eye, which has normal vision and darkvision out to 30 feet. The eye can look in every direction. As an action, you can move the eye up to 30 feet in any direction.",
    classes: ['wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'banishment',
    name: 'Banishment',
    level: 4,
    school: 'abjuration',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'an item distasteful to the target' },
    duration: '1 minute',
    concentration: true,
    description: "You attempt to send one creature that you can see within range to another plane of existence. The target must succeed on a Charisma saving throw or be banished. If the target is native to the plane of existence you're on, you banish the target to a harmless demiplane. While there, the target is incapacitated. The target remains there until the spell ends, at which point the target reappears in the space it left or in the nearest unoccupied space if that space is occupied.",
    higherLevel: "When you cast this spell using a spell slot of 5th level or higher, you can target one additional creature for each slot level above 4th.",
    classes: ['cleric', 'paladin', 'sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'blight',
    name: 'Blight',
    level: 4,
    school: 'necromancy',
    ritual: false,
    castingTime: '1 action',
    range: '30 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "Necromantic energy washes over a creature of your choice that you can see within range, draining moisture and vitality from it. The target must make a Constitution saving throw. The target takes 8d8 necrotic damage on a failed save, or half as much damage on a successful one. This spell has no effect on undead or constructs. If you target a plant creature or a magical plant, it makes the saving throw with disadvantage, and the spell deals maximum damage to it.",
    higherLevel: "When you cast this spell using a spell slot of 5th level or higher, the damage increases by 1d8 for each slot level above 4th.",
    classes: ['druid', 'sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'compulsion',
    name: 'Compulsion',
    level: 4,
    school: 'enchantment',
    ritual: false,
    castingTime: '1 action',
    range: '30 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 minute',
    concentration: true,
    description: "Creatures of your choice that you can see within range and that can hear you must make a Wisdom saving throw. A target automatically succeeds on this saving throw if it can't be charmed. On a failed save, a target is affected by this spell. Until the spell ends, you can use a bonus action on each of your turns to designate a direction that is horizontal to you. Each affected target must use as much of its movement as possible to move in that direction on its next turn.",
    classes: ['bard'],
    source: 'srd-5.1',
  },
  {
    id: 'confusion',
    name: 'Confusion',
    level: 4,
    school: 'enchantment',
    ritual: false,
    castingTime: '1 action',
    range: '90 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'three nut shells' },
    duration: '1 minute',
    concentration: true,
    description: "This spell assaults and twists creatures' minds, spawning delusions and provoking uncontrolled action. Each creature in a 10-foot-radius sphere centered on a point you choose within range must succeed on a Wisdom saving throw when you cast this spell or be affected by it. An affected target can't take reactions and must roll a d10 at the start of each of its turns to determine its behavior for that turn.",
    higherLevel: "When you cast this spell using a spell slot of 5th level or higher, the radius of the sphere increases by 5 feet for each slot level above 4th.",
    classes: ['bard', 'druid', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'conjure-minor-elementals',
    name: 'Conjure Minor Elementals',
    level: 4,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 minute',
    range: '90 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 hour',
    concentration: true,
    description: "You summon elementals that appear in unoccupied spaces that you can see within range. You choose one of the following options for what appears: One elemental of challenge rating 2 or lower, Two elementals of challenge rating 1 or lower, Four elementals of challenge rating 1/2 or lower, or Eight elementals of challenge rating 1/4 or lower.",
    higherLevel: "When you cast this spell using certain higher-level spell slots, you choose one of the summoning options above, and more creatures appear: twice as many with a 6th-level slot and three times as many with an 8th-level slot.",
    classes: ['druid', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'conjure-woodland-beings',
    name: 'Conjure Woodland Beings',
    level: 4,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'one holly berry per creature summoned' },
    duration: '1 hour',
    concentration: true,
    description: "You summon fey creatures that appear in unoccupied spaces that you can see within range. Choose one of the following options for what appears: One fey creature of challenge rating 2 or lower, Two fey creatures of challenge rating 1 or lower, Four fey creatures of challenge rating 1/2 or lower, or Eight fey creatures of challenge rating 1/4 or lower.",
    higherLevel: "When you cast this spell using certain higher-level spell slots, you choose one of the summoning options above, and more creatures appear: twice as many with a 6th-level slot and three times as many with an 8th-level slot.",
    classes: ['druid', 'ranger'],
    source: 'srd-5.1',
  },
  {
    id: 'control-water',
    name: 'Control Water',
    level: 4,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: '300 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a drop of water and a pinch of dust' },
    duration: '10 minutes',
    concentration: true,
    description: "Until the spell ends, you control any freestanding water inside an area you choose that is a cube up to 100 feet on a side. You can choose from any of the following effects when you cast this spell. As an action on your turn, you can repeat the same effect or choose a different one: Flood, Part Water, Redirect Flow, or Whirlpool.",
    classes: ['cleric', 'druid', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'death-ward',
    name: 'Death Ward',
    level: 4,
    school: 'abjuration',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: false },
    duration: '8 hours',
    concentration: false,
    description: "You touch a creature and grant it a measure of protection from death. The first time the target would drop to 0 hit points as a result of taking damage, the target instead drops to 1 hit point, and the spell ends. If the spell is still in effect when the target is subjected to an effect that would kill it instantaneously without dealing damage, that effect is negated against the target, and the spell ends.",
    classes: ['cleric', 'paladin'],
    source: 'srd-5.1',
  },
  {
    id: 'dimension-door',
    name: 'Dimension Door',
    level: 4,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 action',
    range: '500 feet',
    components: { verbal: true, somatic: false, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "You teleport yourself from your current location to any other spot within range. You arrive at exactly the spot desired. It can be a place you can see, one you can visualize, or one you can describe by stating distance and direction. You can bring along objects as long as their weight doesn't exceed what you can carry. You can also bring one willing creature of your size or smaller who is carrying gear up to its carrying capacity.",
    classes: ['bard', 'sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'divination',
    name: 'Divination',
    level: 4,
    school: 'divination',
    ritual: true,
    castingTime: '1 action',
    range: 'Self',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'incense and a sacrificial offering appropriate to your religion, together worth at least 25 gp, which the spell consumes', materialCost: 25, materialConsumed: true },
    duration: 'Instantaneous',
    concentration: false,
    description: "Your magic and an offering put you in contact with a god or a god's servants. You ask a single question concerning a specific goal, event, or activity to occur within 7 days. The DM offers a truthful reply. The reply might be a short phrase, a cryptic rhyme, or an omen.",
    classes: ['cleric'],
    source: 'srd-5.1',
  },
  {
    id: 'dominate-beast',
    name: 'Dominate Beast',
    level: 4,
    school: 'enchantment',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 minute',
    concentration: true,
    description: "You attempt to beguile a beast that you can see within range. It must succeed on a Wisdom saving throw or be charmed by you for the duration. If you or creatures that are friendly to you are fighting it, it has advantage on the saving throw. While the beast is charmed, you have a telepathic link with it as long as the two of you are on the same plane of existence.",
    higherLevel: "When you cast this spell with a 5th-level spell slot, the duration is concentration, up to 10 minutes. When you use a 6th-level spell slot, the duration is concentration, up to 1 hour. When you use a spell slot of 7th level or higher, the duration is concentration, up to 8 hours.",
    classes: ['druid', 'sorcerer'],
    source: 'srd-5.1',
  },
  {
    id: 'fabricate',
    name: 'Fabricate',
    level: 4,
    school: 'transmutation',
    ritual: false,
    castingTime: '10 minutes',
    range: '120 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "You convert raw materials into products of the same material. For example, you can fabricate a wooden bridge from a clump of trees, a rope from a patch of hemp, and clothes from flax or wool. Choose raw materials that you can see within range. You can fabricate a Large or smaller object (contained within a 10-foot cube, or eight connected 5-foot cubes), given a sufficient quantity of raw material.",
    classes: ['wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'fire-shield',
    name: 'Fire Shield',
    level: 4,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: 'Self',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a bit of phosphorus or a firefly' },
    duration: '10 minutes',
    concentration: false,
    description: "Thin and wispy flames wreathe your body for the duration, shedding bright light in a 10-foot radius and dim light for an additional 10 feet. You can end the spell early by using an action to dismiss it. The flames provide you with a warm shield or a chill shield, as you choose. The warm shield grants you resistance to cold damage, and the chill shield grants you resistance to fire damage. Whenever a creature within 5 feet of you hits you with a melee attack, the shield erupts with flame. The attacker takes 2d8 fire damage from a warm shield, or 2d8 cold damage from a cold shield.",
    classes: ['wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'freedom-of-movement',
    name: 'Freedom of Movement',
    level: 4,
    school: 'abjuration',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a leather strap, bound around the arm or a similar appendage' },
    duration: '1 hour',
    concentration: false,
    description: "You touch a willing creature. For the duration, the target's movement is unaffected by difficult terrain, and spells and other magical effects can neither reduce the target's speed nor cause the target to be paralyzed or restrained. The target can also spend 5 feet of movement to automatically escape from nonmagical restraints.",
    classes: ['bard', 'cleric', 'druid', 'ranger'],
    source: 'srd-5.1',
  },
  {
    id: 'giant-insect',
    name: 'Giant Insect',
    level: 4,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: '30 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '10 minutes',
    concentration: true,
    description: "You transform up to ten centipedes, three spiders, five wasps, or one scorpion within range into giant versions of their natural forms for the duration. A centipede becomes a giant centipede, a spider becomes a giant spider, a wasp becomes a giant wasp, and a scorpion becomes a giant scorpion. Each creature obeys your verbal commands.",
    classes: ['druid'],
    source: 'srd-5.1',
  },
  {
    id: 'greater-invisibility',
    name: 'Greater Invisibility',
    level: 4,
    school: 'illusion',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 minute',
    concentration: true,
    description: "You or a creature you touch becomes invisible until the spell ends. Anything the target is wearing or carrying is invisible as long as it is on the target's person.",
    classes: ['bard', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'guardian-of-faith',
    name: 'Guardian of Faith',
    level: 4,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 action',
    range: '30 feet',
    components: { verbal: true, somatic: false, material: false },
    duration: '8 hours',
    concentration: false,
    description: "A Large spectral guardian appears and hovers for the duration in an unoccupied space of your choice that you can see within range. The guardian occupies that space and is indistinct except for a gleaming sword and shield emblazoned with the symbol of your deity. Any creature hostile to you that moves to a space within 10 feet of the guardian for the first time on a turn must succeed on a Dexterity saving throw. The creature takes 20 radiant damage on a failed save, or half as much damage on a successful one. The guardian vanishes when it has dealt a total of 60 damage.",
    classes: ['cleric'],
    source: 'srd-5.1',
  },
  {
    id: 'hallucinatory-terrain',
    name: 'Hallucinatory Terrain',
    level: 4,
    school: 'illusion',
    ritual: false,
    castingTime: '10 minutes',
    range: '300 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a stone, a twig, and a bit of green plant' },
    duration: '24 hours',
    concentration: false,
    description: "You make natural terrain in a 150-foot cube in range look, sound, and smell like some other sort of natural terrain. Thus, open fields or a road can be made to resemble a swamp, hill, crevasse, or some other difficult or impassable terrain. A pond can be made to seem like a grassy meadow, a precipice like a gentle slope, or a rock-strewn gully like a wide and smooth road.",
    classes: ['bard', 'druid', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'ice-storm',
    name: 'Ice Storm',
    level: 4,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: '300 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a pinch of dust and a few drops of water' },
    duration: 'Instantaneous',
    concentration: false,
    description: "A hail of rock-hard ice pounds to the ground in a 20-foot-radius, 40-foot-high cylinder centered on a point within range. Each creature in the cylinder must make a Dexterity saving throw. A creature takes 2d8 bludgeoning damage and 4d6 cold damage on a failed save, or half as much damage on a successful one. Hailstones turn the storm's area of effect into difficult terrain until the end of your next turn.",
    higherLevel: "When you cast this spell using a spell slot of 5th level or higher, the bludgeoning damage increases by 1d8 for each slot level above 4th.",
    classes: ['druid', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'locate-creature',
    name: 'Locate Creature',
    level: 4,
    school: 'divination',
    ritual: false,
    castingTime: '1 action',
    range: 'Self',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a bit of fur from a bloodhound' },
    duration: '1 hour',
    concentration: true,
    description: "Describe or name a creature that is familiar to you. You sense the direction to the creature's location, as long as that creature is within 1,000 feet of you. If the creature is moving, you know the direction of its movement. The spell can locate a specific creature known to you, or the nearest creature of a specific kind (such as a human or a unicorn), so long as you have seen such a creature up close—within 30 feet—at least once.",
    classes: ['bard', 'cleric', 'druid', 'paladin', 'ranger', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'phantasmal-killer',
    name: 'Phantasmal Killer',
    level: 4,
    school: 'illusion',
    ritual: false,
    castingTime: '1 action',
    range: '120 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 minute',
    concentration: true,
    description: "You tap into the nightmares of a creature you can see within range and create an illusory manifestation of its deepest fears, visible only to that creature. The target must make a Wisdom saving throw. On a failed save, the target becomes frightened for the duration. At the end of each of the target's turns before the spell ends, the target must succeed on a Wisdom saving throw or take 4d10 psychic damage. On a successful save, the spell ends.",
    higherLevel: "When you cast this spell using a spell slot of 5th level or higher, the damage increases by 1d10 for each slot level above 4th.",
    classes: ['wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'polymorph',
    name: 'Polymorph',
    level: 4,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a caterpillar cocoon' },
    duration: '1 hour',
    concentration: true,
    description: "This spell transforms a creature that you can see within range into a new form. An unwilling creature must make a Wisdom saving throw to avoid the effect. The spell has no effect on a shapechanger or a creature with 0 hit points. The transformation lasts for the duration, or until the target drops to 0 hit points or dies. The new form can be any beast whose challenge rating is equal to or less than the target's (or the target's level, if it doesn't have a challenge rating).",
    classes: ['bard', 'druid', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'private-sanctum',
    name: 'Private Sanctum',
    level: 4,
    school: 'abjuration',
    ritual: false,
    castingTime: '10 minutes',
    range: '120 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a thin sheet of lead, a piece of opaque glass, a wad of cotton or cloth, and powdered chrysolite' },
    duration: '24 hours',
    concentration: false,
    description: "You make an area within range magically secure. The area is a cube that can be as small as 5 feet to as large as 100 feet on each side. The spell lasts for the duration or until you use an action to dismiss it. You can choose several security features including blocking sound, light, divination spells, and planar travel.",
    higherLevel: "When you cast this spell using a spell slot of 5th level or higher, you can increase the size of the cube by 100 feet for each slot level beyond 4th.",
    classes: ['wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'resilient-sphere',
    name: 'Resilient Sphere',
    level: 4,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: '30 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a hemispherical piece of clear crystal and a matching hemispherical piece of gum arabic' },
    duration: '1 minute',
    concentration: true,
    description: "A sphere of shimmering force encloses a creature or object of Large size or smaller within range. An unwilling creature must make a Dexterity saving throw. On a failed save, the creature is enclosed for the duration. Nothing—not physical objects, energy, or other spell effects—can pass through the barrier, in or out, though a creature in the sphere can breathe there.",
    classes: ['wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'secret-chest',
    name: 'Secret Chest',
    level: 4,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'an exquisite chest, 3 feet by 2 feet by 2 feet, constructed from rare materials worth at least 5,000 gp, and a Tiny replica made from the same materials worth at least 50 gp', materialCost: 5050 },
    duration: 'Instantaneous',
    concentration: false,
    description: "You hide a chest, and all its contents, on the Ethereal Plane. You must touch the chest and the miniature replica that serves as a material component for the spell. The chest can contain up to 12 cubic feet of nonliving material. While the chest remains on the Ethereal Plane, you can use an action and touch the replica to recall the chest.",
    classes: ['wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'stone-shape',
    name: 'Stone Shape',
    level: 4,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'soft clay, which must be worked into roughly the desired shape of the stone object' },
    duration: 'Instantaneous',
    concentration: false,
    description: "You touch a stone object of Medium size or smaller or a section of stone no more than 5 feet in any dimension and form it into any shape that suits your purpose. So, for example, you could shape a large rock into a weapon, idol, or coffer, or make a small passage through a wall, as long as the wall is less than 5 feet thick.",
    classes: ['cleric', 'druid', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'stoneskin',
    name: 'Stoneskin',
    level: 4,
    school: 'abjuration',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'diamond dust worth 100 gp, which the spell consumes', materialCost: 100, materialConsumed: true },
    duration: '1 hour',
    concentration: true,
    description: "This spell turns the flesh of a willing creature you touch as hard as stone. Until the spell ends, the target has resistance to nonmagical bludgeoning, piercing, and slashing damage.",
    classes: ['druid', 'ranger', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'wall-of-fire',
    name: 'Wall of Fire',
    level: 4,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: '120 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a small piece of phosphorus' },
    duration: '1 minute',
    concentration: true,
    description: "You create a wall of fire on a solid surface within range. You can make the wall up to 60 feet long, 20 feet high, and 1 foot thick, or a ringed wall up to 20 feet in diameter, 20 feet high, and 1 foot thick. The wall is opaque and lasts for the duration. When the wall appears, each creature within its area must make a Dexterity saving throw. On a failed save, a creature takes 5d8 fire damage, or half as much damage on a successful save.",
    higherLevel: "When you cast this spell using a spell slot of 5th level or higher, the damage increases by 1d8 for each slot level above 4th.",
    classes: ['druid', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
];
